import { formatDate as localeDate } from 'i18n/format';
import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import StorageIcon from '@mui/icons-material/Storage';
import WavesIcon from '@mui/icons-material/Waves';
import TableChartIcon from '@mui/icons-material/TableChart';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  useWarehouse,
  useWarehouseOverview,
  useUpdateWarehouse,
  useSetWarehouseActive,
  describeDependents,
} from 'api/warehouse';
import { extractApiError, hasFieldErrors, normalizeFieldErrors } from 'api/errors';
import ConfirmActionDialog from 'components/common/ConfirmActionDialog';
import useRole from 'hooks/useRole';
import Panel from 'pages/console/bi-api/Panel';
import { fmtNumber, fmtRelative } from 'pages/console/bi-api/format';
import { LakehouseLinks, LakehouseStats, LoadsPanel } from './LakehouseCard';
import { t } from 'i18n';
import { useTranslation } from 'react-i18next';

function formatDate(iso) {
  if (!iso) return '—';
  return localeDate(new Date(iso), { dateStyle: 'medium', timeStyle: 'short' });
}

export default function WarehouseServiceDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: warehouse, isLoading, error } = useWarehouse(id);

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error || !warehouse) return <Alert severity="error">{extractApiError(error, t('data.failedToLoadWarehouse'))} <Button size="small" onClick={() => navigate('/console/pipelines?tab=storage')}>{t('data.goBack')}</Button></Alert>;

  return warehouse.kind === 'lakehouse'
    ? <LakehouseDetail warehouse={warehouse} />
    : <OwnWarehouseDetail warehouse={warehouse} />;
}

// Enabling is immediate; disabling goes through the type-to-confirm dialog,
// which also explains what still blocks it.
function useActiveToggle(warehouse, onError) {
  const setActive = useSetWarehouseActive();
  const [open, setOpen] = useState(false);
  const [dialogError, setDialogError] = useState(null);

  const run = async (isActive) => {
    onError(null);
    try {
      await setActive.mutateAsync({ id: warehouse.public_id, isActive });
      setOpen(false);
    } catch (err) {
      const msg = extractApiError(err, t('data.failedToUpdateWarehousePleaseTryAgain'));
      if (isActive) onError(msg);
      else setDialogError(msg); // keep the dialog open so the user can retry
    }
  };

  const toggle = () => {
    if (warehouse.is_active) {
      setDialogError(null);
      setOpen(true);
    } else {
      run(true);
    }
  };

  const blocking = !!warehouse.dependents?.blocking;
  const dialog = (
    <ConfirmActionDialog
      open={open}
      title={blocking ? t('data.canTDisableWarehouse') : t('data.disableWarehouse')}
      confirmWord={t('data.actiondisable')}
      confirmLabel={t('data.disableWarehouse')}
      confirmColor="warning"
      blocked={blocking}
      loading={setActive.isPending}
      error={dialogError}
      onConfirm={() => run(false)}
      onClose={() => setOpen(false)}
    >
      {blocking ? (
        <Typography variant="body2">
          <strong>{warehouse.name}</strong>{' '}{t('data.isStillUsedBy')}{' '}
          {describeDependents(warehouse.dependents)}{t('data.disableOrRemoveThoseFirstThenYouCanDisableThisWarehouse')}</Typography>
      ) : (
        <Typography variant="body2">{t('data.disabling')}{' '}<strong>{warehouse.name}</strong>{' '}{t('data.hidesItFromNewPipelinesAndDbtProjects')}{warehouse.kind === 'byo' ? t('data.andFreesItsPlanSlot') : ''}{t('data.nothingCurrentlyRunsAgainstItSoExistingWorkIsnTAffected')}</Typography>
      )}
    </ConfirmActionDialog>
  );

  return { toggle, pending: setActive.isPending, dialog };
}

function Header({ warehouse, icon, chips, actions }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, minWidth: 0 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/console/pipelines?tab=storage')} size="small" color="inherit">{t('data.workspaceData.storageSettings')}</Button>
        <Divider orientation="vertical" flexItem />
        {icon}
        <Box>
          <Typography variant="h5" fontWeight={700}>{warehouse.name}</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
            {chips}
            {!warehouse.is_active && <Chip label={t('data.disabled')} size="small" color="warning" />}
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>{actions}</Box>
    </Box>
  );
}

function FactRows({ rows }) {
  useTranslation();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {rows.map(([label, value, mono]) => (
        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          <Typography variant="body2" fontFamily={mono ? 'monospace' : undefined} sx={{ textAlign: 'right' }}>{value}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// ── Lakehouse ────────────────────────────────────────────────────────────────

function LakehouseDetail({ warehouse }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'settings' ? 'settings' : 'overview';
  const setTab = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'overview') next.delete('tab');
    else next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <Box>
      <Header
        warehouse={warehouse}
        icon={<WavesIcon color="primary" />}
        chips={<Chip label="Lakehouse" size="small" color="primary" />}
      />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab value="overview" label={t('data.overview')} />
        <Tab value="settings" label={t('data.settings')} />
      </Tabs>
      {tab === 'overview' ? <LakehouseOverview warehouse={warehouse} /> : <LakehouseSettings warehouse={warehouse} />}
    </Box>
  );
}

function LakehouseOverview({ warehouse }) {
  const { t } = useTranslation();
  const { data: overview, isLoading, error } = useWarehouseOverview(warehouse.public_id);

  if (error) return <Alert severity="error">{t('data.failedToLoadTheLakehouseOverview')}</Alert>;
  if (isLoading || !overview) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  const lastMaintenance = overview.maintenance.last_run;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <LakehouseStats overview={overview} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}><LoadsPanel loads={overview.loads} /></Grid>
        <Grid item xs={12} md={4}><LakehouseLinks overview={overview} /></Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Panel icon={<TableChartIcon />} title={t('data.largestRecentLoads')}>
            {overview.top_tables.length ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('data.table2')}</TableCell>
                    <TableCell align="right">{t('data.rows')}</TableCell>
                    <TableCell align="right">{t('data.loaded')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {overview.top_tables.map((row) => (
                    <TableRow key={`${row.schema}.${row.table}`}>
                      <TableCell><Typography variant="body2" fontFamily="monospace">{row.schema}.{row.table}</Typography></TableCell>
                      <TableCell align="right">{fmtNumber(row.rows)}</TableCell>
                      <TableCell align="right">{fmtRelative(row.loaded_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary">{t('data.nothingLoadedYet')}</Typography>
            )}
          </Panel>
        </Grid>
        <Grid item xs={12} md={5}>
          <Panel icon={<AccountTreeIcon />} title={t('data.dbtModels')}>
            {overview.tables.dbt_projects.length ? (
              <FactRows
                rows={overview.tables.dbt_projects.map((p) => [
                  p.name,
                  t('data.valueBuiltValueFailedValueNotBuilt', { value1: fmtNumber(p.built), value2: fmtNumber(p.failed), value3: fmtNumber(p.never_built) }),
                ])}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">{t('data.noDbtProjectBuildsOnThisLakehouseYet')}</Typography>
            )}
            <Divider sx={{ my: 2 }} />
            <FactRows
              rows={[
                [
                  t('data.lastTableMaintenance'),
                  lastMaintenance
                    ? `${lastMaintenance.status} · ${fmtRelative(lastMaintenance.finished_at || lastMaintenance.started_at)}`
                    : t('data.notYet'),
                ],
              ]}
            />
          </Panel>
        </Grid>
      </Grid>
    </Box>
  );
}

function LakehouseSettings({ warehouse }) {
  const { t } = useTranslation();
  const { isAdmin } = useRole();
  const navigate = useNavigate();
  const updateWarehouse = useUpdateWarehouse(warehouse.public_id);
  const [name, setName] = useState(warehouse.name);
  const [nameError, setNameError] = useState(null);
  const [error, setError] = useState(null);
  const active = useActiveToggle(warehouse, setError);

  const saveName = async () => {
    if (!name.trim()) { setNameError(t('data.nameIsRequired')); return; }
    setNameError(null);
    setError(null);
    try {
      await updateWarehouse.mutateAsync({ name: name.trim() });
    } catch (err) {
      if (hasFieldErrors(err)) setNameError(normalizeFieldErrors(err.response.data).name);
      else setError(extractApiError(err, t('data.failedToSaveChangesPleaseTryAgain')));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 560 }}>
      {error && <Alert severity="error">{error}</Alert>}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>{t('data.general')}</Typography>
        {isAdmin ? <><Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap', mb: 2 }}>
          <TextField label={t('data.name')} size="small" value={name} onChange={(e) => setName(e.target.value)} fullWidth error={!!nameError} helperText={nameError} />
          <Button
            variant="contained"
            startIcon={updateWarehouse.isPending ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            disabled={updateWarehouse.isPending || name.trim() === warehouse.name}
            onClick={saveName}
          >{t('data.save')}</Button>
        </Box>
        <FormControlLabel
          control={<Switch checked={warehouse.is_active} disabled={active.pending} onChange={active.toggle} />}
          label={warehouse.is_active ? t('data.activePipelinesAndDbtCanUseIt') : t('data.disabled')}
        />
        </> : <FactRows rows={[[t('data.name'), warehouse.name], [t('data.status'), warehouse.is_active ? t('data.active') : t('data.disabled')]]} />}
      </Paper>

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>{t('data.managedByThePlatform')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('data.storageAndAccessAreSetUpAndMaintainedForYouThere')}</Typography>
        <FactRows
          rows={[
            [t('data.type'), t('data.lakehouse')],
            [t('data.tableFormat'), 'Apache Iceberg'],
            [t('data.storage'), t('data.dedicatedToYourWorkspace')],
            [t('data.created2'), formatDate(warehouse.created_at)],
          ]}
        />
      </Paper>

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>{t('data.directSql')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('data.queryTheLakehouseFromBiToolsAndSqlClientsWithA')}</Typography>
        <Button variant="outlined" size="small" onClick={() => navigate('/console/connections')}>{t('data.manageConnections')}</Button>
      </Paper>
      {active.dialog}
    </Box>
  );
}

// ── Bring-your-own warehouse ────────────────────────────────────────────────

function OwnWarehouseDetail({ warehouse }) {
  const { t } = useTranslation();
  const { isAdmin } = useRole();
  const updateWarehouse = useUpdateWarehouse(warehouse.public_id);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [saveError, setSaveError] = useState(null);
  const active = useActiveToggle(warehouse, setSaveError);
  const connection = warehouse.connection;

  const startEdit = () => {
    setForm({ name: warehouse.name, host: connection.host, port: connection.port, database: connection.database, username: connection.username, password: '' });
    setFormErrors({});
    setSaveError(null);
    setIsEditing(true);
  };

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    if (!form.name?.trim()) { setFormErrors({ name: t('data.nameIsRequired') }); return; }
    setFormErrors({});
    setSaveError(null);
    try {
      await updateWarehouse.mutateAsync(form);
      setIsEditing(false);
    } catch (err) {
      if (hasFieldErrors(err)) {
        setFormErrors(normalizeFieldErrors(err.response.data));
      } else {
        setSaveError(extractApiError(err, t('data.failedToSaveChangesPleaseTryAgain')));
      }
    }
  };

  return (
    <Box>
      <Header
        warehouse={warehouse}
        icon={<StorageIcon color="primary" />}
        chips={<Chip label={warehouse.engine_display} size="small" />}
        actions={
          !isAdmin ? null : !isEditing ? (
            <>
              {connection && <Tooltip title={t('data.edit')}><IconButton size="small" onClick={startEdit}><EditIcon fontSize="small" /></IconButton></Tooltip>}
              <Tooltip title={warehouse.is_active ? t('data.disable') : t('data.enable')}><IconButton size="small" color={warehouse.is_active ? 'warning' : 'success'} disabled={active.pending} onClick={active.toggle}>{active.pending ? <CircularProgress size={16} /> : warehouse.is_active ? <BlockIcon fontSize="small" /> : <PowerSettingsNewIcon fontSize="small" />}</IconButton></Tooltip>
            </>
          ) : (
            <>
              <Button variant="contained" size="small" startIcon={updateWarehouse.isPending ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />} onClick={handleSave} disabled={updateWarehouse.isPending}>{updateWarehouse.isPending ? t('data.saving') : t('data.save')}</Button>
              <Button size="small" startIcon={<CancelIcon />} onClick={() => setIsEditing(false)} disabled={updateWarehouse.isPending}>{t('data.cancel')}</Button>
            </>
          )
        }
      />

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3, maxWidth: 520 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>{t('data.connectionDetails')}</Typography>

        {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

        {isEditing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label={t('data.name')} value={form.name} onChange={(e) => set('name', e.target.value)} required fullWidth error={!!formErrors.name} helperText={formErrors.name} />
            <TextField label={t('data.engine')} value={warehouse.engine_display} fullWidth disabled helperText={t('data.theEngineIsFixedOnceAWarehouseIsCreated')} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label={t('data.host')} value={form.host} onChange={(e) => set('host', e.target.value)} required fullWidth error={!!formErrors.host} helperText={formErrors.host} />
              <TextField label={t('data.port')} type="number" value={form.port} onChange={(e) => set('port', e.target.value)} sx={{ width: 120 }} error={!!formErrors.port} helperText={formErrors.port} />
            </Box>
            <TextField label={t('data.database')} value={form.database} onChange={(e) => set('database', e.target.value)} required fullWidth error={!!formErrors.database} helperText={formErrors.database} />
            <TextField label={t('data.username')} value={form.username} onChange={(e) => set('username', e.target.value)} fullWidth />
            <TextField
              label={t('data.password')}
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={(e) => set('password', e.target.value)}
              fullWidth
              placeholder={connection.has_password ? t('data.unchanged') : undefined}
              InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowPassword((s) => !s)} edge="end">{showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment> }}
            />
          </Box>
        ) : (
          <FactRows
            rows={[
              [t('data.engine'), warehouse.engine_display],
              // The connection block reaches tenant admins only.
              ...(connection ? [
                [t('data.host'), `${connection.host}:${connection.port}`, true],
                [t('data.database'), connection.database],
                [t('data.username'), connection.username || '—'],
                [t('data.password'), connection.has_password ? '••••••••' : '—'],
              ] : []),
              [t('data.created2'), formatDate(warehouse.created_at)],
            ]}
          />
        )}
      </Paper>
      {active.dialog}
    </Box>
  );
}
